<template>
  <div class="space-y-6">
    <div class="flex justify-between items-center">
      <h2 class="text-2xl font-bold text-gray-900">Gift Vouchers</h2>
      <button
        @click="openCreateModal"
        class="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors flex items-center space-x-2"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        <span>New Voucher</span>
      </button>
    </div>

    <!-- Info Box -->
    <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <div class="flex items-start">
        <svg class="w-5 h-5 text-blue-400 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
        </svg>
        <div class="text-sm text-blue-800">
          <p class="font-medium">About Gift Vouchers</p>
          <p class="mt-1">Create a voucher and the PDF downloads automatically — ready to email or print. Use <strong>PDF</strong> on any voucher to download it again, or <strong>Print</strong> to send it straight to a printer. Guests redeem it by presenting it at the distillery, or by booking online (no payment required) and presenting the voucher at the start of the tour. Use <strong>Mark redeemed</strong> when a voucher has been used.</p>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-12">
      <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      <p class="mt-4 text-gray-600">Loading vouchers...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="p-4 bg-red-50 border border-red-200 rounded-lg">
      <p class="text-red-800">{{ error }}</p>
    </div>

    <!-- Voucher List -->
    <div v-else class="bg-white border rounded-lg overflow-hidden">
      <div class="bg-gray-50 px-6 py-3 border-b">
        <h3 class="font-semibold text-gray-900">Issued Vouchers</h3>
      </div>
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 text-sm">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">Number</th>
              <th class="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">Experience</th>
              <th class="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">Guests</th>
              <th class="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">Presented to</th>
              <th class="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">Issued</th>
              <th class="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">Valid until</th>
              <th class="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">Status</th>
              <th class="px-4 py-3 text-right font-medium text-gray-500 uppercase tracking-wider text-xs">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr v-for="voucher in vouchers" :key="voucher.number" class="hover:bg-gray-50">
              <td class="px-4 py-3 font-mono font-medium text-gray-900 whitespace-nowrap">{{ voucher.number }}</td>
              <td class="px-4 py-3 text-gray-700 whitespace-nowrap">{{ voucher.experience }}</td>
              <td class="px-4 py-3 text-gray-700">{{ voucher.guests }}</td>
              <td class="px-4 py-3 text-gray-700">{{ voucher.presentedTo || '—' }}</td>
              <td class="px-4 py-3 text-gray-700 whitespace-nowrap">{{ formatDate(voucher.issuedDate) }}</td>
              <td class="px-4 py-3 whitespace-nowrap" :class="isExpired(voucher) ? 'text-red-600 font-medium' : 'text-gray-700'">
                {{ formatDate(voucher.validUntil) }}
              </td>
              <td class="px-4 py-3">
                <span
                  :class="[
                    'px-2 py-1 text-xs rounded-full font-medium whitespace-nowrap',
                    voucher.status === 'redeemed' ? 'bg-gray-200 text-gray-700' :
                    isExpired(voucher) ? 'bg-red-100 text-red-800' :
                    'bg-green-100 text-green-800'
                  ]"
                >
                  {{ voucher.status === 'redeemed' ? 'Redeemed' : isExpired(voucher) ? 'Expired' : 'Active' }}
                </span>
              </td>
              <td class="px-4 py-3 text-right whitespace-nowrap">
                <button
                  @click="downloadPdf(voucher)"
                  :disabled="downloadingNumber !== null"
                  class="text-blue-600 hover:text-blue-800 font-medium mr-3 disabled:opacity-50"
                  title="Download the voucher as a PDF file"
                >
                  {{ downloadingNumber === voucher.number ? 'Generating…' : 'PDF' }}
                </button>
                <button
                  @click="printVoucher(voucher)"
                  class="text-blue-600 hover:text-blue-800 font-medium mr-3"
                  title="Open the voucher in a new tab and print it"
                >
                  Print
                </button>
                <button
                  @click="openEditModal(voucher)"
                  class="text-gray-600 hover:text-black font-medium mr-3"
                  title="Edit recipient and expiry date"
                >
                  Edit
                </button>
                <button
                  v-if="voucher.status !== 'redeemed'"
                  @click="setStatus(voucher, 'redeemed')"
                  class="text-gray-600 hover:text-black font-medium mr-3"
                >
                  Mark redeemed
                </button>
                <button
                  v-else
                  @click="setStatus(voucher, 'issued')"
                  class="text-gray-600 hover:text-black font-medium mr-3"
                >
                  Reactivate
                </button>
                <button
                  @click="removeVoucher(voucher)"
                  class="text-red-600 hover:text-red-800"
                  title="Delete"
                >
                  <svg class="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </td>
            </tr>
            <tr v-if="vouchers.length === 0">
              <td colspan="8" class="px-6 py-8 text-center text-gray-500">No gift vouchers yet</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Edit Voucher Modal -->
    <div
      v-if="showEditModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      @click.self="closeEditModal"
    >
      <div class="bg-white rounded-lg p-6 max-w-md w-full">
        <div class="flex justify-between items-start mb-4">
          <h3 class="text-xl font-bold">Edit Voucher {{ editVoucher.number }}</h3>
          <button @click="closeEditModal" class="text-gray-400 hover:text-gray-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form @submit.prevent="saveEdit" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Presented to <span class="text-gray-400 font-normal">(optional)</span></label>
            <input
              v-model="editVoucher.presentedTo"
              type="text"
              placeholder="Leave empty for a blank line to handwrite"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">With compliments of <span class="text-gray-400 font-normal">(optional)</span></label>
            <input
              v-model="editVoucher.complimentsOf"
              type="text"
              placeholder="Leave empty for a blank line to handwrite"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Valid Until</label>
            <input
              v-model="editVoucher.validUntil"
              type="date"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
            />
          </div>

          <div v-if="editError" class="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p class="text-sm text-red-800">{{ editError }}</p>
          </div>

          <div class="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              @click="closeEditModal"
              class="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="savingEdit"
              class="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              {{ savingEdit ? 'Saving...' : 'Save Changes' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Create Voucher Modal -->
    <div
      v-if="showCreateModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      @click.self="closeCreateModal"
    >
      <div class="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div class="flex justify-between items-start mb-4">
          <h3 class="text-xl font-bold">New Gift Voucher</h3>
          <button @click="closeCreateModal" class="text-gray-400 hover:text-gray-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form @submit.prevent="createVoucher" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Experience</label>
            <select
              v-model="newVoucher.experience"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
            >
              <option value="Tour and Tasting">Tour and Tasting</option>
              <option value="Cellar Door Tasting">Cellar Door Tasting</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Number of Guests</label>
            <input
              v-model.number="newVoucher.guests"
              type="number"
              min="1"
              max="16"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Presented to <span class="text-gray-400 font-normal">(optional)</span></label>
            <input
              v-model="newVoucher.presentedTo"
              type="text"
              placeholder="Leave empty for a blank line to handwrite"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">With compliments of <span class="text-gray-400 font-normal">(optional)</span></label>
            <input
              v-model="newVoucher.complimentsOf"
              type="text"
              placeholder="Leave empty for a blank line to handwrite"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
            />
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Issue Date</label>
              <input
                v-model="newVoucher.issuedDate"
                type="date"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Valid Until</label>
              <input
                v-model="newVoucher.validUntil"
                type="date"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
              />
            </div>
          </div>

          <div v-if="createError" class="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p class="text-sm text-red-800">{{ createError }}</p>
          </div>

          <div class="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              @click="closeCreateModal"
              class="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="creating"
              class="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              {{ creating ? 'Creating...' : 'Create & Download PDF' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { format, parseISO } from 'date-fns'
import { useAuth } from '../../composables/useAuth'
import { buildVoucherHtml } from '../../utils/voucherTemplate.js'

const { getAuthAxios } = useAuth()

const vouchers = ref([])
const loading = ref(false)
const error = ref(null)

const showCreateModal = ref(false)
const creating = ref(false)
const createError = ref(null)

const defaultDates = () => {
  const today = new Date()
  const expiry = new Date(today)
  expiry.setFullYear(expiry.getFullYear() + 1)
  return {
    issuedDate: format(today, 'yyyy-MM-dd'),
    validUntil: format(expiry, 'yyyy-MM-dd')
  }
}

const newVoucher = ref({
  experience: 'Tour and Tasting',
  guests: 2,
  presentedTo: '',
  complimentsOf: '',
  ...defaultDates()
})

const openCreateModal = () => {
  newVoucher.value = {
    experience: 'Tour and Tasting',
    guests: 2,
    presentedTo: '',
    complimentsOf: '',
    ...defaultDates()
  }
  createError.value = null
  showCreateModal.value = true
}

const closeCreateModal = () => {
  showCreateModal.value = false
  createError.value = null
}

const fetchVouchers = async () => {
  loading.value = true
  error.value = null

  try {
    const authAxios = getAuthAxios()
    const response = await authAxios.get('/api/admin-vouchers')
    if (response.data.success) {
      vouchers.value = response.data.vouchers
    }
  } catch (err) {
    error.value = err.response?.data?.error || 'Failed to load vouchers'
    console.error('Error fetching vouchers:', err)
  } finally {
    loading.value = false
  }
}

const createVoucher = async () => {
  creating.value = true
  createError.value = null

  try {
    const authAxios = getAuthAxios()
    const response = await authAxios.post('/api/admin-vouchers', newVoucher.value)

    if (response.data.success) {
      vouchers.value.unshift(response.data.voucher)
      closeCreateModal()
      downloadPdf(response.data.voucher)
    }
  } catch (err) {
    createError.value = err.response?.data?.error || 'Failed to create voucher'
    console.error('Error creating voucher:', err)
  } finally {
    creating.value = false
  }
}

const showEditModal = ref(false)
const savingEdit = ref(false)
const editError = ref(null)
const editVoucher = ref({ number: '', presentedTo: '', complimentsOf: '', validUntil: '' })

const openEditModal = (voucher) => {
  editVoucher.value = {
    number: voucher.number,
    presentedTo: voucher.presentedTo || '',
    complimentsOf: voucher.complimentsOf || '',
    validUntil: voucher.validUntil
  }
  editError.value = null
  showEditModal.value = true
}

const closeEditModal = () => {
  showEditModal.value = false
  editError.value = null
}

const saveEdit = async () => {
  savingEdit.value = true
  editError.value = null

  try {
    const authAxios = getAuthAxios()
    const response = await authAxios.patch('/api/admin-vouchers', editVoucher.value)

    if (response.data.success) {
      const index = vouchers.value.findIndex(v => v.number === response.data.voucher.number)
      if (index >= 0) vouchers.value.splice(index, 1, response.data.voucher)
      closeEditModal()
    }
  } catch (err) {
    editError.value = err.response?.data?.error || 'Failed to update voucher'
    console.error('Error updating voucher:', err)
  } finally {
    savingEdit.value = false
  }
}

const setStatus = async (voucher, status) => {
  try {
    const authAxios = getAuthAxios()
    const response = await authAxios.patch('/api/admin-vouchers', {
      number: voucher.number,
      status
    })
    if (response.data.success) {
      const index = vouchers.value.findIndex(v => v.number === voucher.number)
      if (index >= 0) vouchers.value.splice(index, 1, response.data.voucher)
    }
  } catch (err) {
    alert(err.response?.data?.error || 'Failed to update voucher')
  }
}

const removeVoucher = async (voucher) => {
  if (!confirm(`Delete voucher ${voucher.number}? This cannot be undone.`)) return

  try {
    const authAxios = getAuthAxios()
    const response = await authAxios.delete(`/api/admin-vouchers?number=${encodeURIComponent(voucher.number)}`)
    if (response.data.success) {
      vouchers.value = vouchers.value.filter(v => v.number !== voucher.number)
    }
  } catch (err) {
    alert(err.response?.data?.error || 'Failed to delete voucher')
  }
}

const formatDate = (dateStr) => {
  try {
    return format(parseISO(dateStr), 'MMM d, yyyy')
  } catch {
    return dateStr
  }
}

const isExpired = (voucher) => {
  return voucher.validUntil < format(new Date(), 'yyyy-MM-dd')
}

// --- Voucher document actions ---

const printVoucher = (voucher) => {
  const printWindow = window.open('', '_blank')
  if (!printWindow) {
    alert('Please allow pop-ups to print the voucher')
    return
  }
  printWindow.document.write(buildVoucherHtml(voucher, { origin: window.location.origin }))
  printWindow.document.close()
}

const downloadingNumber = ref(null)

const downloadPdf = async (voucher) => {
  if (downloadingNumber.value) return
  downloadingNumber.value = voucher.number

  try {
    const authAxios = getAuthAxios()
    const response = await authAxios.get(
      `/api/admin-voucher-pdf?number=${encodeURIComponent(voucher.number)}`,
      { responseType: 'blob', timeout: 60000 }
    )

    const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
    const link = document.createElement('a')
    link.href = url
    link.download = `83-islands-gift-voucher-${voucher.number}.pdf`
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  } catch (err) {
    console.error('Error downloading voucher PDF:', err)
    alert('Failed to generate the PDF. You can still use Print and save as PDF from the dialog.')
  } finally {
    downloadingNumber.value = null
  }
}

onMounted(() => {
  fetchVouchers()
})
</script>
